import React, { useContext, useEffect, useState } from "react"
import { observer } from "mobx-react"
import {
  Anchor,
  Button,
  Code,
  Group,
  Image,
  Modal,
  Paper,
  Stack,
  Text,
  Title
} from "@mantine/core"
import { AppWorkspaceContext } from "../../stores/AppWorkspace"
import {
  DISCORD_URL,
  GITHUB_URL,
  NWC_PATH,
  SUPPORT_EMAIL,
  SWAP_PROVIDER_PATH,
  TELEGRAM_HANDLE,
  TELEGRAM_URL
} from "../../helpers/constants"
import { FrameworkGrid } from "../integrations/FrameworkGrid"
import { PlatformGrid } from "../platforms/PlatformGrid"
import { ProcessorGrid } from "../processors/ProcessorGrid"
import { LearnGrid } from "../learn/LearnGrid"
import { agentDirectionsFor, CATALOG, FRAMEWORKS, frameworkForStack, LANDING_COPY, PLATFORMS } from "../../../docs/catalog"
import type { AgentStack } from "../../../docs/types"
import { CopyAgentDirectionsButtons } from "../integrations/CopyAgentDirectionsButtons"
import { APP_GROUPS, groupOf, STORES, type GroupId } from "../../helpers/frameworkGroups"
import {
  IconArrowsExchange,
  IconBrandDiscord,
  IconBrandGithub,
  IconBrandTelegram,
  IconCurrencyBitcoin,
  IconLock,
  IconMail,
  IconRobot,
  IconShield
} from "@tabler/icons-react"
import { ReleaseInfo } from "../ReleaseInfo"
import { startShopCable } from "../../shop/cable"
import { TrackedDemoShop } from "../TrackedDemoShop"
import { SiteVideo } from "../SiteVideo"
import { guidesVideo, homepageVideo } from "../../helpers/siteVideo"
import { HomeFaq } from "../HomeFaq"
import { MobileHandoff } from "../MobileHandoff"

// Coin Rush stays out of production until it launches (the user, 2026-10-29):
// the section renders only with ?game=true in the URL, and as its own lazy
// chunk, so no visitor without the flag downloads any of it (promo, key art or
// game). To launch, drop the flag check and import CoinRushPromo directly again.
const CoinRushPromo = React.lazy(() =>
  import(/* webpackChunkName: "coin-rush-promo" */ "../CoinRushPromo").then((m) => ({ default: m.CoinRushPromo }))
)
const gamePreview = () => new URLSearchParams(window.location.search).get("game") === "true"

// The phone-only chip row under the hero's buttons: the stacks the ad traffic
// engages with most, each straight to its own page (stack pages engage about
// three times as often as the home page). A platform row is a platform page.
const PHONE_STACKS: { id: string; label: string; kind: "framework" | "platform" }[] = [
  { id: "laravel", label: "Laravel", kind: "framework" },
  { id: "nextjs", label: "Next.js", kind: "framework" },
  { id: "woocommerce", label: "WooCommerce", kind: "framework" },
  { id: "django", label: "Django", kind: "framework" },
  { id: "express", label: "Express", kind: "framework" },
  { id: "replit", label: "Replit", kind: "platform" }
]

const ALBY_URL = "https://getalby.com/"
// Served through the project's CloudFront distribution in front of the
// openreceive-public bucket, never from the S3 origin directly.
const DEMO_VIDEO_URL = "https://d1jtpn4jh9ij1m.cloudfront.net/videos/basic-rails-demo-compressed-2.mp4"
const DEMO_VIDEO_POSTER_URL = "https://d1jtpn4jh9ij1m.cloudfront.net/videos/basic-rails-demo-web.webp"

type Coin = { icon: string; label: string }

type InboundCurrency = {
  payWith: Coin & { name: string }
  networks: Coin[]
}

const INBOUND_CURRENCIES: InboundCurrency[] = [
  {
    payWith: { icon: "usdt", label: "USDT", name: "Tether" },
    networks: [
      { icon: "trx", label: "Tron" },
      { icon: "sol", label: "Solana" },
      { icon: "eth", label: "Ethereum" }
    ]
  },
  {
    payWith: { icon: "usdc", label: "USDC", name: "USD Coin" },
    networks: [
      { icon: "sol", label: "Solana" },
      { icon: "eth", label: "Ethereum" }
    ]
  },
  {
    payWith: { icon: "sol", label: "SOL", name: "Solana" },
    networks: [{ icon: "sol", label: "Solana" }]
  },
  {
    payWith: { icon: "eth", label: "ETH", name: "Ether" },
    networks: [{ icon: "eth", label: "Ethereum" }]
  }
]

// Every stack the generator publishes directions for, labelled from the same
// catalog. New stacks appear in the picker as soon as they ship — nothing here
// names node or rails.
const PLATFORM_LABELS: Record<string, string> = Object.fromEntries(
  CATALOG.platforms.map((platform) => [platform.id, platform.label])
)

// Payloads for an app rather than a server, and their row labels.
const APP_LABELS: Record<string, string> = {
  mobile: "iOS and Android"
}

// One row per stack inside the copy modal. Whatever the generator publishes
// gets a button — nothing here names node or rails either. The label is the
// framework whose page copies that payload ("Express" for the node stack),
// falling back to the platform label, then the payload's own title.
const AGENT_DIRECTION_ROWS = CATALOG.agentDirections.map((entry) => ({
  stack: entry.stack as AgentStack,
  framework: frameworkForStack(entry.stack as AgentStack)?.id ?? entry.stack,
  label:
    frameworkForStack(entry.stack as AgentStack)?.label ??
    PLATFORMS.find((row) => row.id === entry.stack)?.label ??
    PLATFORM_LABELS[entry.stack] ??
    APP_LABELS[entry.stack] ??
    entry.title.replace(/^Agent Directions: /, "")
}))

// The modal's groups below its "start here" default: the homepage grid's
// groups (helpers/frameworkGroups), then the AI app builders, whose
// directions are written for the builder's own agent, then mobile apps,
// which take the payment on their server's checkout page.
type DirectionsGroupId = GroupId | "builders" | "mobile"
type DirectionsFilter = "all" | DirectionsGroupId

const DIRECTIONS_GROUPS: { id: DirectionsGroupId; chip: string; heading: string; note?: string }[] = [
  STORES,
  ...APP_GROUPS,
  { id: "builders", chip: "AI app builders", heading: "AI app builders" },
  {
    id: "mobile",
    chip: "Mobile",
    heading: "Mobile apps",
    note:
      "React Native, Expo, Flutter, Swift or Kotlin. These add OpenReceive to the app\u2019s server " +
      "with that server\u2019s own directions, then make the app open its checkout page."
  }
]

// The contract's platform rows of kind ai-builder (Lovable, Bolt), not
// CATALOG.platforms, which lists language runtimes and names node and rails.
const AI_BUILDER_IDS = new Set(PLATFORMS.filter((row) => row.kind === "ai-builder").map((row) => row.id))

const directionsGroupOf = (row: (typeof AGENT_DIRECTION_ROWS)[number]): DirectionsGroupId => {
  if (APP_LABELS[row.stack] !== undefined) return "mobile"
  const framework = frameworkForStack(row.stack)
  if (framework) return groupOf(framework)
  return AI_BUILDER_IDS.has(row.stack) ? "builders" : "stores"
}

const rowsIn = (group: DirectionsGroupId) => AGENT_DIRECTION_ROWS.filter((row) => directionsGroupOf(row) === group)

// The four-up strip under the hero. Each one is the headline of a paragraph in
// the detail section below, so the two stay worded the same on purpose. The
// words are config/landing_copy.json's home.points, which the server body
// renders too; the icons go with them in this order.
const HERO_POINT_ICONS = [IconCurrencyBitcoin, IconLock, IconShield, IconArrowsExchange]
const HERO = LANDING_COPY.home.hero
const HERO_POINTS = LANDING_COPY.home.points.map((point, index) => ({
  ...point,
  icon: HERO_POINT_ICONS[index] ?? IconShield
}))

export const HomePage: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext)
  // The hero's primary action opens this, and every stack the generator
  // publishes gets its own button inside it. No picker to arm first: choosing a
  // stack and copying it are the same click, and a reader who does not know
  // which stack they want can see all of them at once.
  const [directionsOpen, setDirectionsOpen] = useState(false)
  const [directionsFilter, setDirectionsFilter] = useState<DirectionsFilter>("all")

  // One row of the copy modal: the payload's name, size and links, then its
  // two copy buttons.
  const directionsRow = (row: (typeof AGENT_DIRECTION_ROWS)[number]) => {
    const directions = agentDirectionsFor(row.stack)
    return (
      <Group
        key={row.stack}
        className="or-agent-row"
        justify="space-between"
        align="center"
        gap="sm"
        wrap="wrap"
      >
        <div>
          <Text fw={700}>{row.label}</Text>
          <Text size="xs" c="dimmed">
            {(directions.bytes / 1024).toFixed(1)} KB ·{" "}
            <Anchor href={directions.path} target="_blank" rel="noopener noreferrer">
              markdown
            </Anchor>
            {directions.guidePath ? (
              <>
                {" · "}
                <Anchor
                  href={directions.guidePath}
                  onClick={(event) => {
                    event.preventDefault()
                    setDirectionsOpen(false)
                    workspace.openHref(directions.guidePath as string)
                  }}
                >
                  page
                </Anchor>
              </>
            ) : null}
          </Text>
        </div>
        <CopyAgentDirectionsButtons
          stack={row.stack}
          framework={row.framework}
          ctaLocation="homepage_agent_modal"
          size="sm"
        />
      </Group>
    )
  }

  // The shop's realtime transport, plugged into the stores' push seams for as
  // long as the page that shows the shop is mounted. The shop itself knows
  // nothing about websockets; see app/shop/cable.ts. Push is the fast path and
  // never the only one — both stores keep polling — so a browser that navigates
  // to the docs and back loses a few seconds of latency and nothing else.
  useEffect(() => startShopCable(workspace.shop), [workspace.shop])

  const coinIcon = (name: string, alt: string, size: number) => {
    const src = workspace.iconUrl(name)
    if (!src) return null
    return <Image src={src} alt={alt} w={size} h={size} className="or-coin-icon" />
  }

  const techtalk = guidesVideo()

  return (
    /* pt={0}, not py: .or-hero declares its own padding-top, and stacking the
       Stack's 32px on top of it put ~55px of dead paper between the header and
       the badges. The bottom padding is still wanted, above the footer. */
    <Stack gap={48} pt={0} pb="xl">
      {/* Two columns above the fold: the claim on the left, the artwork on the
          right. The artwork carries the only picture of what the network does,
          so it is described rather than hidden. The brand mark is not repeated
          here — the header already carries it. */}
      <section className="or-hero">
        <div className="or-hero-copy">
          {/* The first thing read above the fold, before the claim itself: this
              is software you can take, not a service you sign up for. */}
          <Group className="or-hero-badges" gap={8} mb="md">
            {HERO.badges.map((badge, index) =>
              index === 0 ? (
                <span className="or-hero-badge" key={badge}>
                  <IconBrandGithub size={16} stroke={1.8} aria-hidden="true" />
                  {badge}
                </span>
              ) : (
                <span className="or-hero-badge or-hero-badge-quiet" key={badge}>
                  {badge}
                </span>
              )
            )}
          </Group>
          {/* The h1 says what this does in the words people search for; the
              tagline above it names the project, and says it is software. */}
          <Text className="or-hero-tagline">{HERO.tagline}</Text>
          <Title className="or-hero-title">{HERO.title}</Title>
          <Text className="or-lede or-hero-lede">
            {HERO.lede}
            <br />
            <strong>{HERO.ledeStrong}</strong> {HERO.ledeTail}
          </Text>

          <Group className="or-hero-actions" gap="sm" mt="xl">
            {/* The primary action above the fold: almost nobody integrating
                this types the code by hand any more, so the first button hands
                an agent everything it needs. It opens the modal rather than
                copying — the stack is chosen there, in the same click. */}
            <Button
              className="or-hero-cta-primary"
              size="md"
              leftSection={<IconRobot size={18} />}
              onClick={() => setDirectionsOpen(true)}
            >
              Copy agent directions
            </Button>
            {/* No Guides or API reference buttons: the header links both, and
                with them here GitHub wrapped onto a second line on desktop. */}
            <Button
              component="a"
              href={GITHUB_URL}
              variant="default"
              size="md"
              target="_blank"
              rel="noopener noreferrer"
              leftSection={<IconBrandGithub size={18} />}
            >
              GitHub
            </Button>
          </Group>

          {/* Phones only (spa.css): the agent directions above are for a
              computer, so a phone visitor gets the demo, a way straight to
              their stack, and the search to run when they are back at a desk. */}
          <div className="or-hero-phone">
            <Button component="a" href="#demo" variant="default" size="md" fullWidth>
              Try the demo checkout
            </Button>
            <nav className="or-hero-stacks" aria-label="Your stack">
              {PHONE_STACKS.map((stack) => {
                const href = stack.kind === "framework" ? `/integrations/${stack.id}` : `/platforms/${stack.id}`
                const logo =
                  stack.kind === "framework" ? workspace.frameworkLogoUrl(stack.id) : workspace.platformLogoUrl(stack.id)
                return (
                  <a
                    key={stack.id}
                    href={href}
                    onClick={(event) => {
                      event.preventDefault()
                      if (stack.kind === "framework") workspace.goIntegration(stack.id)
                      else workspace.goPlatforms(stack.id)
                    }}
                  >
                    {logo ? <img src={logo} alt="" width={18} height={18} /> : null}
                    {stack.label}
                  </a>
                )
              })}
              <a href="#frameworks" className="is-more">
                All {FRAMEWORKS.length} <span aria-hidden="true">→</span>
              </a>
            </nav>
            <MobileHandoff pageKind="home" pageId="home" />
          </div>

          {/* Where to get help, above the fold so nobody has to scroll to find
              a human. Deliberately NOT another outlined button: support is not
              an action on the product. One quiet ruled row instead — icon plus
              a label that says where each link goes. */}
          <div className="or-hero-support">
            <span className="or-hero-support-label">Support</span>
            <a
              className="or-hero-support-link"
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              title={`${TELEGRAM_HANDLE} on Telegram`}
            >
              <IconBrandTelegram size={16} stroke={1.8} aria-hidden="true" />
              Telegram
            </a>
            <a className="or-hero-support-link" href={`mailto:${SUPPORT_EMAIL}`}>
              <IconMail size={16} stroke={1.8} aria-hidden="true" />
              {SUPPORT_EMAIL}
            </a>
            <a
              className="or-hero-support-link"
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <IconBrandDiscord size={16} stroke={1.8} aria-hidden="true" />
              Discord
            </a>
          </div>
        </div>

        {/* The explainer where the mascot was (the user, 2026-10-20), in the
            browser's language: the long cut beside the copy on desktop, the
            short portrait one under it on a phone. Click to play only. */}
        <SiteVideo
          video={homepageVideo()}
          caption="What OpenReceive does, in one video"
          label="What OpenReceive is and how it works"
          sourceLocation="homepage"
          className="or-hero-video"
        />
      </section>

      <ul className="or-hero-points">
        {HERO_POINTS.map((point) => {
          const Icon = point.icon
          return (
            <li className="or-hero-point" key={point.title}>
              <span className="or-hero-point-icon" aria-hidden="true">
                <Icon size={22} stroke={1.6} />
              </span>
              <div>
                <Text className="or-hero-point-title">{point.title}</Text>
                <Text className="or-hero-point-body">{point.body}</Text>
              </div>
            </li>
          )
        })}
      </ul>

      {/* The game (the user, 2026-10-27): one screen down from the top, key
          art and a Play button; the game itself loads only on Play. */}
      {gamePreview() && (
        <React.Suspense fallback={null}>
          <CoinRushPromo />
        </React.Suspense>
      )}

      <section className="or-hero-detail">
        <Text className="or-lede">
          {/* The round Bitcoin mark, same btc.svg the currency chips use, sitting
              inline as the first glyph of the sentence. */}
          <Image
            src={workspace.iconUrl("btc")}
            alt=""
            w={20}
            h={20}
            className="or-lede-bitcoin"
          />
          <strong>Bitcoin Lightning by default.</strong>
            {" "}Your server issues a QR code. The payer pays the QR code, and your server approves
          delivery of the purchase.
        </Text>
        {/* Highlighted on purpose: this is the one property a merchant cares
            about before anything else, so it gets a box instead of a paragraph. */}
        <aside className="or-safety" aria-labelledby="or-safety-title">
          {/* h2, not h3: nothing between the h1 headline and this is an h2, and
              a skipped level fails the heading-order audit. The size is fixed
              in .or-safety-title, so the level is semantic only. */}
          <Title order={2} id="or-safety-title" className="or-safety-title">
            <IconShield size={22} stroke={2} aria-hidden="true" />
            Safety First
          </Title>
          <Text className="or-safety-body">
            <strong>Deposit-only by design.</strong> Even if your server is compromised, nobody can
            spend your funds. OpenReceive has no payment-sending API and never holds spending keys.
            It can only receive.
          </Text>
          <Text className="or-safety-body">
            Receive with any <Anchor href={NWC_PATH}>NWC service</Anchor>, or run your own.
          </Text>
        </aside>
        <Text className="or-lede">
          <strong>Optionally swap in other currencies.</strong>
        </Text>

        {/* Directly under the lead sentence, so the claim and the evidence for it
            read as one unit and the explanation follows both. Inline, not a
            table: every row settled in Bitcoin and the old heading restated the
            sentence above it, so the grid spent five lines of vertical weight on
            two facts. Both now live in the prose, and what is left is the only
            thing a reader scans for — which coin, on which networks. */}
        <Group component="ul" className="or-currencies" gap="xs" mb="xs">
          {INBOUND_CURRENCIES.map((row) => (
            <Group
              component="li"
              key={row.payWith.label}
              className="or-currency"
              gap={6}
              wrap="nowrap"
            >
              {coinIcon(row.payWith.icon, row.payWith.label, 18)}
              <Text span fw={700} size="sm">
                {row.payWith.label}
              </Text>
              <Text span size="sm" c="dimmed">
                {row.networks.map((network) => network.label).join(" · ")}
              </Text>
            </Group>
          ))}
        </Group>

        {/* Which swap APIs work, and how to run your own, is the setup guide's
            business; the homepage only says it can be done. */}
        <Text className="or-lede">
          Set up a <Anchor href={SWAP_PROVIDER_PATH}>swap provider</Anchor> and customers can pay in
          any of these. Every payment still reaches you in Bitcoin.
        </Text>

        {/* The recording sits above the live demo so a visitor sees the whole
            flow once before touching it: a plain Rails app, a QR code, a paid
            invoice. No autoplay, so the page is silent until they ask. */}
        <div className="or-demo-video">
          <Title order={2} className="or-demo-title">
            Watch an OpenReceive checkout in action
          </Title>

          <video
            className="or-demo-video-player"
            src={DEMO_VIDEO_URL}
            poster={DEMO_VIDEO_POSTER_URL}
            controls
            playsInline
            preload="metadata"
            aria-label="Demo of OpenReceive taking a payment in a basic Rails app"
          >
            <Anchor href={DEMO_VIDEO_URL}>Watch the demo video</Anchor>
          </video>
        </div>

        {/* #demo is the live shop, as on the framework pages: the phone
            hero's "Try the demo checkout" and the /demo short link land here,
            not on the recording above it. */}
        <div className="or-demo-intro" id="demo">
          <Title order={2} className="or-demo-title">
            Try this demo
          </Title>
          {/* The prices are real, so say up front that trying it costs
              nothing: the QR code is as far as most visitors need to go. */}
          <Text className="or-demo-lede">
            Put a button in the cart and check out. You get the checkout your customers would see,
            from choosing how to pay to the QR code. You don&rsquo;t have to pay to try it; if you
            do, it&rsquo;s a real payment for a virtual button.
          </Text>
        </div>

        <TrackedDemoShop
          shop={workspace.shop}
          sourceLocation="homepage_demo"
        />
      </section>

      {/* The questions a visitor asks before picking a stack, above the
          grid that picks one. */}
      <HomeFaq />

      {/* After the demo and the FAQ: the framework pages, one per contract row.
          Each carries the full setup for its stack — credentials, environment
          variables, install, wiring — so the homepage only helps the visitor
          pick one. The stepped quickstart and the Quick setup panel that used
          to sit here are gone. */}
      <FrameworkGrid />

      {/* Where people build and host (contract v8), in the frameworks' style.
          Below them while only Vercel and v0 have pages; see PlatformGrid for
          when it moves above. */}
      <PlatformGrid />

      {/* For merchants who already take cards: keep the processor, add BTC, USDT
          & USDC next to it (config/processors.json). */}
      <ProcessorGrid />

      {/* The /learn articles: guides and comparisons for a merchant still
          deciding, under the grids for one who has decided. */}
      <LearnGrid />

      {/* The /guides techtalk again, under the processors (the user,
          2026-10-21). Its plays report source_location "homepage" with
          video "techtalk"; ?t= stays with /guides, where the Clips point. */}
      {techtalk ? (
        <SiteVideo
          video={techtalk}
          title="How OpenReceive works, end to end"
          label="How OpenReceive works: a 20-minute technical deep dive"
          sourceLocation="homepage"
          className="or-home-techtalk"
        />
      ) : null}

      <ReleaseInfo />

      <Text size="sm" c="dimmed" ta="center">
        <Anchor href="/guides" onClick={(event) => { event.preventDefault(); workspace.goGuides() }}>
          Browse every guide
        </Anchor>
        {" · "}
        <Anchor href="/api_docs" onClick={(event) => { event.preventDefault(); workspace.goApiDocs() }}>
          API reference
        </Anchor>
        {" · "}
        <Anchor href="/contact">Contact</Anchor>
        {" · "}
        <Anchor href="/brand">Brand assets</Anchor>
      </Text>

      {/* One pair of buttons per stack, so the choice and the copy are a single
          click: the directions themselves, or the link to their .md file. Each
          row also links the same bytes as a file and as a page, for a reader
          who would rather skim them before handing them to an agent. */}
      <Modal
        opened={directionsOpen}
        onClose={() => setDirectionsOpen(false)}
        title="Copy agent directions"
        centered
        size="lg"
      >
        <Stack gap="lg">
          {/* The default, first and largest: for someone who does not know what
              their site is built with, or has no site yet. The agent works out
              the stack (or picks one) and fetches that stack's directions. */}
          <div className="or-agent-start">
            <Text className="or-eyebrow">Start here</Text>
            <Text fw={800} size="xl">
              Any project, or none yet
            </Text>
            <Text size="sm" c="dimmed" mb="sm">
              Not sure what your site is built with, or starting from scratch? Paste these into
              Cursor, Claude Code, Codex or your site builder&rsquo;s agent. It works out your stack,
              or picks one, and follows the right directions.
            </Text>
            <CopyAgentDirectionsButtons
              stack="start"
              framework="start"
              ctaLocation="homepage_agent_modal"
              size="md"
            />
          </div>

          <div>
            <Text fw={700}>Or pick your stack</Text>
            <Text size="sm" c="dimmed">
              Each one is complete on its own, quickstart included, so an agent with no network
              access can still finish.
            </Text>
            <div className="or-framework-filters" role="group" aria-label="Show directions for">
              {[
                { id: "all" as DirectionsFilter, chip: "All", count: AGENT_DIRECTION_ROWS.length },
                ...DIRECTIONS_GROUPS.map((group) => ({ ...group, count: rowsIn(group.id).length }))
              ]
                .filter((chip) => chip.count > 0)
                .map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    className="or-framework-filter"
                    aria-pressed={directionsFilter === chip.id}
                    onClick={() => setDirectionsFilter(chip.id)}
                  >
                    {chip.chip}
                    <span className="or-framework-filter-count">{chip.count}</span>
                  </button>
                ))}
            </div>
          </div>

          {DIRECTIONS_GROUPS.filter(
            (group) => (directionsFilter === "all" || directionsFilter === group.id) && rowsIn(group.id).length > 0
          ).map((group) => (
            <div key={group.id} className="or-agent-group">
              <Text className="or-agent-group-title">{group.heading}</Text>
              {group.note ? (
                <Text size="sm" c="dimmed" mb="xs">
                  {group.note}
                </Text>
              ) : null}
              {rowsIn(group.id).map(directionsRow)}
            </div>
          ))}

          {/* Supabase is a database, not a stack: each server stack's directions
              carry a Supabase branch in their database step. */}
          <Text size="sm" c="dimmed">
            Database on Supabase? Pick your server&rsquo;s stack; its directions cover Supabase.
          </Text>
        </Stack>
      </Modal>
    </Stack>
  )
})
